// Library Methods
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  TouchSensor,
  useSensor,
  useSensors,
  rectIntersection,
  DragEndEvent,
  DragStartEvent,
  UniqueIdentifier,
  CollisionDetection,
  PointerSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
} from "@dnd-kit/sortable";

// Components
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import DraggableExamCard from "./DraggableExamCard/DraggableExamCard";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Contexts
import { useExamSliderContext } from "src/contexts/exams/ExamSliderContext";

// Constants
import { examCardEnums } from "src/constants/enums";

// Types
import { SelectedExamCardType } from "src/services/exams/types";

const PackageOrderSection = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);
  const { selectedExamCards, actions } = useExamSliderContext();
  const { setSelectedExamCards, onRemoveCard } = actions;

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  const formattedExamCards: Array<SelectedExamCardType & { label: string }> =
    useMemo(
      () =>
        selectedExamCards?.map((selectedExam) => {
          const enumValues = Object.values(examCardEnums);

          const examCardValue = enumValues.find(
            (enumValue) =>
              enumValue.algorithmPath === selectedExam?.examFieldType
          );

          const label =
            examCardValue && "buttonLabel" in examCardValue
              ? examCardValue.buttonLabel
              : examCardValue?.label;

          return {
            ...selectedExam,
            label: label ?? "",
          };
        }),
      [selectedExamCards]
    );

  // SortableContext needs exam ids in a string array
  const examOrderIds: Array<string> = useMemo(
    () =>
      selectedExamCards.map(
        (exam) => `${exam?.examFieldType}-${exam?.groupId}`
      ),
    [selectedExamCards]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor)
  );

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id);
  }

  // Update exam order when dragging cards
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      setActiveId(null);
      return;
    }

    setSelectedExamCards((prev: Array<SelectedExamCardType>) => {
      const oldIndex = prev.findIndex(
        (exam) => `${exam.examFieldType}-${exam.groupId}` === active.id
      );
      const newIndex = prev.findIndex(
        (exam) => `${exam.examFieldType}-${exam.groupId}` === over.id
      );
      const newExamOrder = arrayMove(prev, oldIndex, newIndex);

      // When exams of the same type are moved around,
      // Update the numbering to reflect the new order
      let indexNums: { [key: string]: number } = {};
      const updatedGroupIds = newExamOrder.map((exam, index) => {
        const hasMultiSameExams =
          prev.filter(
            (prevExam) => prevExam.examFieldType === exam.examFieldType
          )?.length > 1;

        if (hasMultiSameExams) {
          indexNums = {
            ...indexNums,
            [exam.examFieldType]: (indexNums?.[exam.examFieldType] ?? 0) + 1,
          };

          return {
            ...exam,
            groupId: index,
            examNumLabel: indexNums?.[exam.examFieldType],
          };
        } else {
          return { ...exam, groupId: index };
        }
      });

      return [...updatedGroupIds];
    });

    setActiveId(null);
  }

  function handleDragCancel() {
    setActiveId(null);
  }

  const fixCursorSnapOffset: CollisionDetection = (args) => {
    // Return if keyboard activated
    if (!args.pointerCoordinates) {
      return rectIntersection(args);
    }
    const { x, y } = args.pointerCoordinates;
    const { width, height } = args.collisionRect;
    const updated = {
      ...args,
      // The collision rectangle is broken when using snapCenterToCursor
      // Reset the collision rectangle based on pointer location and overlay size
      collisionRect: {
        width,
        height,
        bottom: y + height / 2,
        left: x - width / 2,
        right: x + width / 2,
        top: y - height / 2,
      },
    };
    return rectIntersection(updated);
  };

  return (
    <Grid item display="flex" flexDirection="column" gap="4px" width="100%">
      <Typography fontSize="12px">{t("Package Order")}</Typography>

      {selectedExamCards?.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={fixCursorSnapOffset}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext items={examOrderIds} strategy={rectSortingStrategy}>
            {/* <Typography color={onSurface} fontSize="12px">
                {t("Drag and drop each card to change the package order.")}
              </Typography> */}
            <Grid
              item
              display="flex"
              flexWrap="nowrap"
              gap="8px"
              sx={{ width: "100%", minWidth: 0 }}
            >
              {formattedExamCards.map((item, index) => {
                const hasMultiSameExams =
                  formattedExamCards?.filter(
                    (exam) =>
                      item.examFieldType !==
                        examCardEnums.VIDEO.algorithmPath &&
                      exam.examFieldType === item.examFieldType
                  )?.length > 1;

                const onRemoveCardWrapper = () => {
                  onRemoveCard(item.examFieldType, item.groupId);
                };

                const getLabel = () => {
                  if (hasMultiSameExams) {
                    return `#${item?.examNumLabel ?? index + 1} - ${t(item?.label)}`;
                  }

                  if (
                    item.examFieldType === examCardEnums.VIDEO.algorithmPath
                  ) {
                    return item.videoName ?? t(item?.label);
                  }

                  return t(item?.label);
                };

                return (
                  <DraggableExamCard
                    key={`${item?.examFieldType}-${item?.groupId}`}
                    id={`${item?.examFieldType}-${item?.groupId}`}
                    label={getLabel()}
                    orderNumber={index + 1}
                    isDragMode={activeId !== null}
                    onRemoveCard={onRemoveCardWrapper}
                  />
                );
              })}
            </Grid>
          </SortableContext>
        </DndContext>
      ) : (
        <Typography color={onSurface} fontSize="12px">
          {t("Nothing is Selected.")}
        </Typography>
      )}
    </Grid>
  );
};

export default PackageOrderSection;
